<sb-dialog-activation
  [showTitle]="currentStep === PayrollDialogStep.TEASER"
  [showBackButton]="currentStep > 0 && currentStep !== PayrollDialogStep.SUMMARY"
  [showProgressBar]="currentStep > 0 && currentStep !== PayrollDialogStep.SUMMARY"
  [currentStep]="currentStepProgress"
  [totalSteps]="steps"
  (back)="goBack()"
>
  <div main-panel>
    @switch (currentStep) {
      @case (PayrollDialogStep.TEASER) {
        <chapter-step
          [subtitle]="'Payroll' | translate"
          [title]="'Automate your payroll between your provider and Shiftbase.' | translate"
          [nextButtonLabel]="'Start' | translate"
          (next)="goToStep(PayrollDialogStep.SELECT_PAYROLL_TOOL)"
        >
          <div class="flex flex-col gap-1">
            <chapter-item [text]="'Integrate with your payroll provider of choice in minutes' | translate" />
            <chapter-item [text]="'No more managing your payroll with excel' | translate" />
            <chapter-item [text]="'All major payroll providers supported' | translate" />
          </div>
        </chapter-step>
      }
      @case (PayrollDialogStep.SELECT_PAYROLL_TOOL) {
        <chapter-step
          [title]="'Which payroll tool do you or your bookkeeper use?' | translate"
          [nextButtonDisabled]="!selectedPayrollTool"
          [skipButtonLabel]="'I don\'t use a tool for payroll' | translate"
          [showSkipButton]="true"
          (next)="onSelectPayrollToolNext()"
          (skip)="onNoPayrollToolSelected()"
        >
          <sb-radio-badge-group data-testid="payroll-tool-input" [(ngModel)]="selectedPayrollTool">
            @for (option of payrollToolOptions; track option.value) {
              <sb-radio-badge [value]="option.value">{{ option.label ?? option.value }}</sb-radio-badge>
            }
          </sb-radio-badge-group>
        </chapter-step>
      }
      @case (PayrollDialogStep.SPECIFIC_TOOL) {
        <chapter-step
          [title]="
            'Almost there! We\'ll email you instructions to connect [selectedPayrollTool] with Shiftbase.'
              | translate: { selectedPayrollTool }
          "
          [nextButtonLabel]="'Okay, thanks' | translate"
          [nextButtonLoading]="nextButtonLoading"
          (next)="goToStep(PayrollDialogStep.SUMMARY)"
        >
          <p
            class="m-0 text-base"
            [innerHTML]="
              'Check your inbox at [email] for the next steps.' | translate: { email: email$ | async | bold }
            "
          ></p>
        </chapter-step>
      }
      @case (PayrollDialogStep.OTHER_TOOL) {
        <chapter-step
          [title]="
            'Great to hear you\'re using a payroll solution! We\'re always adding new integrations. Which one do you use?'
              | translate
          "
          [nextButtonDisabled]="!payrollTool"
          [nextButtonLoading]="nextButtonLoading"
          [skipButtonLoading]="skipButtonLoading"
          [showSkipButton]="true"
          (next)="goToStep(PayrollDialogStep.SUMMARY, true)"
          (skip)="goToStep(PayrollDialogStep.SUMMARY, true, 'skip')"
        >
          <sb-form-field [label]="'Your payroll tool' | translate">
            <input type="text" name="payroll_tool" [(ngModel)]="payrollTool" sbInput maxlength="50" />
          </sb-form-field>
        </chapter-step>
      }
      @case (PayrollDialogStep.NO_TOOL) {
        <chapter-step
          [title]="
            'That\'s okay if you don\'t use a tool — Shiftbase can still help with payroll. Choosing a tool later can make things easier:'
              | translate
          "
          [nextButtonLabel]="'Got it!' | translate"
          [nextButtonLoading]="nextButtonLoading"
          (next)="goToStep(PayrollDialogStep.SUMMARY, true)"
        >
          <div class="flex flex-col gap-1">
            <chapter-item [text]="'Prevent costly reporting errors' | translate" />
            <chapter-item [text]="'Avoid the risk of outdated records' | translate" />
            <chapter-item [text]="'Generate accurate reports in seconds' | translate" />
          </div>
          <p class="m-0 text-base">
            <span class="font-bold">
              {{ 'Need help choosing?' | translate }}
            </span>
            {{ 'Our support team is here for you anytime.' | translate }}
          </p>
        </chapter-step>
      }
      @case (PayrollDialogStep.UNKNOWN_TOOL) {
        <chapter-step
          [title]="'That\'s okay, your bookkeeper should know what payroll tool you use.' | translate"
          [nextButtonLabel]="'Send me the email' | translate"
          [nextButtonLoading]="nextButtonLoading"
          (next)="goToStep(PayrollDialogStep.SUMMARY)"
          [showSkipButton]="true"
          [skipButtonLoading]="skipButtonLoading"
          (skip)="goToStep(PayrollDialogStep.SUMMARY, true, 'skip')"
        >
          <p
            class="text-base"
            [innerHTML]="
              'We can send you an email to [email] that you can forward to them with next steps and more info.'
                | translate: { email: email$ | async | bold }
            "
          ></p>
        </chapter-step>
      }
      @case (PayrollDialogStep.SUMMARY) {
        <chapter-step
          main-panel
          [title]="'🎉 ' + ('That\'s it for now!' | translate)"
          [nextButtonLabel]="chapterCTAs?.primary?.label || 'Continue exploring' | translate"
          (next)="close({ action: 'primary' })"
        >
          <p class="m-0 text-base">
            @if (skipEmail) {
              {{
                'You can always learn more about how we can streamline your payroll from our help centre.' | translate
              }}
            } @else {
              {{
                "We've emailed information about getting started with payroll. Start inviting employees." | translate
              }}
            }
            <a
              [href]="'integrations' | knowledgeBaseArticleLink"
              target="_blank"
              class="font-bold text-brand"
              data-testid="article-link"
            >
              {{ 'Learn more about Payroll' | translate }}
            </a>
          </p>
        </chapter-step>
      }
    }
  </div>
  <div
    class="flex size-full min-h-72 overflow-hidden rounded-lg bg-cover bg-center bg-no-repeat lg:mr-12"
    aside-panel
    [ngStyle]="{
      backgroundImage: 'url(assets/images/onboarding/chapters/payroll.jpg)',
    }"
  ></div>
</sb-dialog-activation>
